import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getEmployeeAssignments } from '@/pages/shiftManagement/services/scheduleManagement.services';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Shield, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const DAYS_OF_WEEK = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const DAYS_LABELS = { lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo' };
const BASE_WEEKLY_HOURS = 44;
const MAX_OVERTIME_HOURS = 12;
const MAX_WEEKLY_HOURS = 56;

// Página: Mis Turnos (Operario) — Nuevo calendario de asignaciones
const OperatorShiftsPage = () => {
  const { idLavanderia } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  // Datos del operario proporcionados por OperatorDashboard (state) o por query params; fallback: usuario autenticado
  const stateOpName = location.state?.operatorName || location.state?.name || '';
  const stateOpCedula = location.state?.operatorCedula || location.state?.cedula || '';
  const url = new URL(window.location.href);
  const qpName = url.searchParams.get('name') || '';
  const qpCed = url.searchParams.get('cedula') || '';
  const userCed = user?.raw?.cedula || user?.raw?.empleado?.cedula || user?.cedula || '';
  const userName = user?.raw?.empleado
    ? `${user.raw.empleado.nombre || ''} ${user.raw.empleado.apellido || ''}`.trim()
    : (user?.raw?.nombre || user?.nombre || '');
  const operatorName = (stateOpName || qpName || userName || '').trim();
  const operatorCedula = (stateOpCedula || qpCed || userCed || '').trim();
  const employeeId = user?.raw?.empleado?.id || user?.raw?.empleado?._id || user?.empleadoId || null;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEndDate = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekStartStr = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEndDate }), [weekStart, weekEndDate]);

  const displayName = operatorName || userName || '—';
  const displayCedula = operatorCedula || userCed || '—';

  // ─── Cálculos de horas ──────────────────────────────────────────
  const calcTotalHours = (horarios, almuerzo) => {
    if (!horarios) return 0;
    let total = 0;
    for (const day of DAYS_OF_WEEK) {
      const ranges = horarios[day] || [];
      // Solo procesar almuerzo si el día tiene rangos de trabajo
      if (ranges.length > 0) {
        for (const range of ranges) {
          const [hI, mI] = (range.inicio || '00:00').split(':').map(Number);
          const [hF, mF] = (range.fin || '00:00').split(':').map(Number);
          total += (hF * 60 + mF - hI * 60 - mI) / 60;
        }
        // Restar minutos de almuerzo del día (no cuentan como hora trabajada)
        const lunchMin = Number(almuerzo?.[day]) || 0;
        if (lunchMin > 0) {
          total -= lunchMin / 60;
        }
      }
    }
    return Math.round(total * 10) / 10;
  };

  const calcBreakdown = (horarios, almuerzo) => {
    const total = calcTotalHours(horarios, almuerzo);
    const ordinarias = Math.min(total, BASE_WEEKLY_HOURS);
    const extras = Math.max(0, Math.round((total - BASE_WEEKLY_HOURS) * 10) / 10);
    const alerts = [];
    if (extras > 0 && extras <= MAX_OVERTIME_HOURS) {
      alerts.push({ type: 'warning', msg: `${extras}h extras esta semana` });
    }
    if (extras > MAX_OVERTIME_HOURS) {
      alerts.push({ type: 'danger', msg: `Excede máx. extras (${extras}h / ${MAX_OVERTIME_HOURS}h permitidas)` });
    }
    if (total > MAX_WEEKLY_HOURS) {
      alerts.push({ type: 'danger', msg: `Excede ${MAX_WEEKLY_HOURS}h semanales permitidas` });
    }
    return { total, ordinarias: Math.round(ordinarias * 10) / 10, extras, alerts };
  };

  const calcDayHours = (ranges, lunchMin) => {
    if (!ranges || ranges.length === 0) return 0;
    let h = 0;
    for (const r of ranges) {
      const [hI, mI] = (r.inicio || '00:00').split(':').map(Number);
      const [hF, mF] = (r.fin || '00:00').split(':').map(Number);
      h += (hF * 60 + mF - hI * 60 - mI) / 60;
    }
    // Restar almuerzo si hay rangos
    const lunch = Number(lunchMin) || 0;
    if (lunch > 0) {
      h -= lunch / 60;
    }
    return Math.max(0, Math.round(h * 10) / 10);
  };

  // ─── Cargar asignaciones ────────────────────────────────────────
  const loadAssignments = useCallback(async () => {
    const lavanderiaId = idLavanderia || user?.lavanderia?.id || user?.lavanderia?._id || user?.lavanderia?.codigo || '';
    if (!lavanderiaId) { setAssignments([]); return; }
    if (!operatorCedula && !employeeId) { setAssignments([]); return; }
    try {
      setLoading(true);
      setError('');
      const res = await getEmployeeAssignments(lavanderiaId, weekStartStr, {
        cedula: operatorCedula || undefined,
        employeeId: employeeId || undefined,
      });
      setAssignments(Array.isArray(res) ? res : []);
    } catch (err) {
      setError(err?.message || 'No se pudo cargar la programación semanal');
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [idLavanderia, user, weekStartStr, operatorCedula, employeeId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // ─── Totales globales (si hay múltiples asignaciones/roles) ─────
  const globalBreakdown = useMemo(() => {
    // Merge all horarios and almuerzo into one for total calculation
    const merged = {};
    const mergedAlmuerzo = {};
    for (const day of DAYS_OF_WEEK) {
      merged[day] = [];
      mergedAlmuerzo[day] = 0;
    }
    for (const a of assignments) {
      for (const day of DAYS_OF_WEEK) {
        merged[day] = [...merged[day], ...(a.horarios?.[day] || [])];
        // Solo tomar el almuerzo si hay rangos en este día para esta asignación
        if ((a.horarios?.[day] || []).length > 0) {
          mergedAlmuerzo[day] = Math.max(mergedAlmuerzo[day], a.almuerzo?.[day] || 0);
        }
      }
    }
    return calcBreakdown(merged, mergedAlmuerzo);
  }, [assignments]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));

  return (
    <div className="  space-y-4">
      <div className="mb-2 flex items-center gap-2">
        <Button asChild variant="outline">
          <Link to="/operario-dashboard"><ArrowLeft className="mr-2 h-4 w-4" /> Volver</Link>
        </Button>
        <h1 className="ml-2 text-2xl font-bold">Mis Turnos</h1>
      </div>
     

      {/* Navegación semanal */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <CardTitle className="text-center text-md font-bold" >{displayName}  </CardTitle>
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <Button variant="outline" size="icon" className="h-10 w-10 md:h-8 md:w-8" onClick={handlePrevWeek} disabled={loading}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <span className="flex-1 text-center sm:flex-none font-semibold text-muted-foreground text-sm md:text-base">
                {format(weekDays[0], 'd MMM', { locale: es })} - {format(weekDays[6], 'd MMM, yyyy', { locale: es })}
              </span>
              <Button variant="outline" size="icon" className="h-10 w-10 md:h-8 md:w-8" onClick={handleNextWeek} disabled={loading}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

          {loading && <div className="text-sm text-muted-foreground py-4">Cargando programación…</div>}

          {!loading && assignments.length === 0 && !error && (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No hay turnos programados para esta semana.</p>
            </div>
          )}

          {/* Resumen de horas */}
          {!loading && assignments.length > 0 && (
            <div className="space-y-4">
              {/* Tarjetas resumen */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">{globalBreakdown.ordinarias}h</p>
                  <p className="text-xs text-muted-foreground">Horas Ordinarias</p>
                </div>
                <div className="border rounded-lg p-3 text-center">
                  <p className={`text-2xl font-bold ${globalBreakdown.extras > 0 ? 'text-amber-600 dark:text-amber-400' : ''}`}>{globalBreakdown.extras}h</p>
                  <p className="text-xs text-muted-foreground">Horas Extras</p>
                </div>
{/*                 <div className="border rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{globalBreakdown.total}h</p>
                  <p className="text-xs text-muted-foreground">Total Semanal</p>
                </div>
                <div className="border rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{assignments.length}</p>
                  <p className="text-xs text-muted-foreground">{assignments.length === 1 ? 'Asignación' : 'Asignaciones'}</p>
                </div> */}
              </div>

              {/* Alertas globales */}
{/*               {globalBreakdown.alerts.length > 0 && (
                <div className="space-y-1">
                  {globalBreakdown.alerts.map((al, i) => (
                    <div key={i} className={`flex items-center gap-2 text-sm rounded-md px-3 py-2 ${al.type === 'danger' ? 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400' : 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400'}`}>
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      {al.msg}
                    </div>
                  ))}
                </div>
              )} */}

              {/* Detalle por asignación (rol) */}
              {assignments.map((a, aIdx) => {
                const bd = calcBreakdown(a.horarios, a.almuerzo);
                return (
                  <Card key={a._id || aIdx} className="border">
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">Rol:</span>
                        <div className="flex items-center gap-1.5">
                          {a.shiftRole?.color && (
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: a.shiftRole.color }} />
                          )}
                          <span className="font-medium">{a.shiftRole?.nombre || 'Sin rol'}</span>
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{a.scheduleType?.nombre || 'Sin horario'}</span>
                        </div>
                        <Badge variant="secondary" className="ml-auto text-xs">{bd.total}h</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
                        {DAYS_OF_WEEK.map((day, dIdx) => {
                          const ranges = a.horarios?.[day] || [];
                          const lunchMin = a.almuerzo?.[day] || 0;
                          const dayH = calcDayHours(ranges, lunchMin);
                          const dateObj = weekDays[dIdx];
                          const dateStr = dateObj ? format(dateObj, 'dd/MM', { locale: es }) : '';
                          const isToday = dateObj && format(dateObj, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                          return (
                            <div
                              key={day}
                              className={`border rounded-lg p-2.5 transition-colors ${isToday ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : ''} ${ranges.length === 0 ? 'bg-muted/30' : ''}`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className={`text-xs font-semibold ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                                  {DAYS_LABELS[day]}
                                </span>
                                <span className="text-[10px] text-muted-foreground">{dateStr}</span>
                              </div>
                              {ranges.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic">Libre</p>
                              ) : (
                                <div className="space-y-1">
                                  {ranges.map((r, rIdx) => (
                                    <div key={rIdx} className="flex items-center gap-1">
                                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                      <span className="text-xs font-medium">{r.inicio} - {r.fin}</span>
                                    </div>
                                  ))}
                                  {lunchMin > 0 && (
                                    <div className="text-[10px] text-amber-600 dark:text-amber-400 italic">
                                      Almuerzo: {lunchMin}min
                                    </div>
                                  )}
                                  <div className="text-[10px] text-muted-foreground font-medium mt-1 pt-1 border-t">
                                    {dayH}h
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Alertas de esta asignación */}
                      {bd.alerts.length > 0 && assignments.length > 1 && (
                        <div className="mt-2 space-y-1">
                          {bd.alerts.map((al, i) => (
                            <div key={i} className={`flex items-center gap-1 text-xs ${al.type === 'danger' ? 'text-red-600' : 'text-amber-600'}`}>
                              <AlertTriangle className="h-3 w-3 shrink-0" />
                              {al.msg}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Leyenda */}
              <div className="text-xs text-muted-foreground flex items-center gap-4 pt-2">
                <span>Base semanal: {BASE_WEEKLY_HOURS}h</span>
                <span>Máx. extras: {MAX_OVERTIME_HOURS}h</span>
                <span>Máx. total: {MAX_WEEKLY_HOURS}h</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperatorShiftsPage;
